"use client";

import { OFFSTREET_COMPANY_LOGO } from "@offstreetparking/design-system/providers";
import { useQuery } from "@tanstack/react-query";
import { useParker } from "~/components/providers/AuthProvider";
import { useCompany } from "~/components/providers/CompanyProvider";
import { ConnectCompanyPrompt } from "./ConnectCompanyPrompt";
import { ParkerSetupPrompt } from "./ParkerSetupPrompt";
import { SelectDefaultVehiclePrompt } from "./SelectDefaultVehiclePrompt";
import { requestParkerSetup } from "./actions";

export const ParkerUserOverlay = ({ hideParkerSetup = false }: { hideParkerSetup?: boolean }) => {
	const { needsParkerCreation, isConnectedParker, isLoggedIn, refreshAuth, parker } = useParker();
	const { company } = useCompany();
	const { isError, refetch } = useQuery({
		queryKey: ["parker-creation-refresh-auth", needsParkerCreation],
		enabled: needsParkerCreation,
		retry: 3,
		retryDelay: 5000,
		refetchInterval: 15_000,
		queryFn: async () => await refreshAuth(),
	});

	if (needsParkerCreation && !isConnectedParker) {
		return hideParkerSetup ? null : (
			<ParkerSetupPrompt isError={isError} onRetry={() => void requestParkerSetup().then(() => refetch())} />
		);
	}

	if (parker?.defaultVehicle && !parker.defaultVehicle.isDefault) {
		return <SelectDefaultVehiclePrompt />;
	}

	if (!isLoggedIn || needsParkerCreation || !company) {
		return null;
	}

	const logo = company.branding?.companyLogoFileUrl
		? { url: company.branding.companyLogoFileUrl, altText: company.name }
		: { url: OFFSTREET_COMPANY_LOGO, altText: "Offstreet" };

	return <ConnectCompanyPrompt companyId={company.id} companyName={company.name} logo={logo} />;
};
